// Pure helpers for RCS templates: form <-> JioCX payload, validation, and the
// send-side helpers. No React in this file.

export const TEMPLATE_TYPES = [
  { value: "basic", label: "Basic" },
  { value: "standalone", label: "Rich card" },
  { value: "carousel", label: "Carousel" },
];

export const typeLabel = (type) =>
  TEMPLATE_TYPES.find((t) => t.value === type)?.label || type || "";

export const MAX_VALUE_LENGTH = 40;

export const displayFormattedDate = (dateStr) => {
  if (!dateStr) return "";
  const regex = /^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2} (AM|PM)$/i;
  if (regex.test(dateStr)) {
    return dateStr;
  }
  const parsedDate = new Date(dateStr);
  if (!isNaN(parsedDate.getTime())) {
    const pad = (n) => String(n).padStart(2, "0");
    const day = pad(parsedDate.getDate());
    const month = pad(parsedDate.getMonth() + 1);
    const year = parsedDate.getFullYear();

    let hours = parsedDate.getHours();
    const minutes = pad(parsedDate.getMinutes());
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    const formattedHours = pad(hours);

    return `${day}/${month}/${year} ${formattedHours}:${minutes} ${ampm}`;
  }
  return dateStr;
};

// ---------------------------------------------------------------------------
// Form state
// ---------------------------------------------------------------------------

export const emptySuggestion = (kind = "reply") => ({
  kind,
  text: "",
  postback: "",
  url: "",
  phone: "",
  startTime: "{{1}}",
  endTime: "{{2}}",
  title: "",
  description: "",
});

export const emptyCard = () => ({
  title: "",
  description: "",
  mediaUrl: "",
  mediaHeight: "MEDIUM",
  thumbnailUrl: "",
  suggestions: [],
});

export const emptyForm = (type = "basic") => ({
  type,
  template_name: "",
  basic: { text: "", suggestions: [] },
  standalone: { orientation: "VERTICAL", card: emptyCard() },
  carousel: { cardWidth: "MEDIUM_WIDTH", cards: [emptyCard(), emptyCard()] },
});

// ---------------------------------------------------------------------------
// buildTemplateData(form) -> JioCX `data`
// ---------------------------------------------------------------------------

const slug = (text) =>
  String(text || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

export const defaultPostback = (text, index) =>
  `${slug(text) || "button"}_${index + 1}`;

const buildSuggestion = (s, index) => {
  const plainText = s.text;
  const postBack = { data: s.postback || defaultPostback(s.text, index) };
  switch (s.kind) {
    case "openUrl":
      return { action: { plainText, postBack, openUrl: { url: s.url } } };
    case "dial":
      return {
        action: { plainText, postBack, dialerAction: { phoneNumber: s.phone } },
      };
    case "calendar":
      return {
        action: {
          plainText,
          postBack,
          createCalendarEvent: {
            startTime: s.startTime,
            endTime: s.endTime,
            title: s.title,
            description: s.description,
          },
        },
      };
    case "reply":
    default:
      return { reply: { plainText, postBack } };
  }
};

const buildSuggestions = (list = []) => list.map(buildSuggestion);

const buildCard = (card) => {
  const out = {};
  if (card.title) out.cardTitle = card.title;
  if (card.description) out.cardDescription = card.description;
  if (card.mediaUrl) {
    const contentInfo = { fileUrl: card.mediaUrl };
    if (card.thumbnailUrl) contentInfo.thumbnailUrl = card.thumbnailUrl;
    out.cardMedia = { mediaHeight: card.mediaHeight, contentInfo };
  }
  if (card.suggestions?.length) out.suggestions = buildSuggestions(card.suggestions);
  return out;
};

export const buildTemplateData = (form) => {
  switch (form.type) {
    case "standalone":
      return {
        content: {
          richCardDetails: {
            standalone: {
              cardOrientation: form.standalone.orientation,
              content: buildCard(form.standalone.card),
            },
          },
        },
      };
    case "carousel":
      return {
        content: {
          richCardDetails: {
            carousel: {
              cardWidth: form.carousel.cardWidth,
              contents: form.carousel.cards.map(buildCard),
            },
          },
        },
      };
    case "basic":
    default: {
      const content = { plainText: form.basic.text };
      if (form.basic.suggestions.length) {
        content.suggestions = buildSuggestions(form.basic.suggestions);
      }
      return { content };
    }
  }
};

// ---------------------------------------------------------------------------
// formFromData(data, name) -> form (reverse of buildTemplateData, for
// "Create new version")
// ---------------------------------------------------------------------------

const suggestionFromData = (s) => {
  if (s?.reply) {
    return {
      ...emptySuggestion("reply"),
      text: s.reply.plainText || "",
      postback: s.reply.postBack?.data || "",
    };
  }
  const a = s?.action || {};
  const base = {
    text: a.plainText || "",
    postback: a.postBack?.data || "",
  };
  if (a.openUrl) return { ...emptySuggestion("openUrl"), ...base, url: a.openUrl.url || "" };
  if (a.dialerAction) {
    return { ...emptySuggestion("dial"), ...base, phone: a.dialerAction.phoneNumber || "" };
  }
  if (a.createCalendarEvent) {
    const ev = a.createCalendarEvent;
    return {
      ...emptySuggestion("calendar"),
      ...base,
      startTime: ev.startTime || "",
      endTime: ev.endTime || "",
      title: ev.title || "",
      description: ev.description || "",
    };
  }
  return { ...emptySuggestion("reply"), ...base };
};

const cardFromData = (c = {}) => ({
  title: c.cardTitle || "",
  description: c.cardDescription || "",
  mediaUrl: c.cardMedia?.contentInfo?.fileUrl || "",
  mediaHeight: c.cardMedia?.mediaHeight || "MEDIUM",
  thumbnailUrl: c.cardMedia?.contentInfo?.thumbnailUrl || "",
  suggestions: (c.suggestions || []).map(suggestionFromData),
});

export const formFromData = (data, templateName = "") => {
  const content = data?.content || {};
  const standalone = content.richCardDetails?.standalone;
  const carousel = content.richCardDetails?.carousel;

  if (standalone) {
    const form = emptyForm("standalone");
    form.standalone = {
      orientation: standalone.cardOrientation || "VERTICAL",
      card: cardFromData(standalone.content),
    };
    return { ...form, template_name: templateName };
  }
  if (carousel) {
    const form = emptyForm("carousel");
    const cards = (carousel.contents || []).map(cardFromData);
    while (cards.length < 2) cards.push(emptyCard());
    form.carousel = { cardWidth: carousel.cardWidth || "MEDIUM_WIDTH", cards };
    return { ...form, template_name: templateName };
  }
  const form = emptyForm("basic");
  form.basic = {
    text: content.plainText || "",
    suggestions: (content.suggestions || []).map(suggestionFromData),
  };
  return { ...form, template_name: templateName };
};

// ---------------------------------------------------------------------------
// validateTemplateForm(form) -> { [fieldKey]: message }
// ---------------------------------------------------------------------------

const PLACEHOLDER = /\{\{(\d+)\}\}/g;
const ANY_BRACES = /\{\{([^}]*)\}\}/g;
const ADJACENT = /\{\{\d+\}\}\s*\{\{\d+\}\}/;
const SINGLE_VAR = /^\{\{\d+\}\}$/;
const URL_RE = /^https?:\/\/\S+$/i;

export const countVariables = (str) => (String(str || "").match(PLACEHOLDER) || []).length;

// Checks placeholder syntax and numbering for one text field. Returns an error
// message or null. `sequence: false` skips the 1..n rule (calendar times, which
// are a single variable each).
const checkVariables = (str, { sequence = true } = {}) => {
  const text = String(str || "");
  const all = [...text.matchAll(ANY_BRACES)];
  if (all.some((m) => !/^\d+$/.test(m[1]))) return "Use {{1}}, {{2}}…";
  if (/\{\{|\}\}/.test(text.replace(PLACEHOLDER, ""))) return "Use {{1}}, {{2}}…";

  const nums = all.map((m) => Number(m[1]));
  if (sequence && nums.length) {
    const unique = new Set(nums);
    const contiguous = nums.every((n) => n >= 1 && n <= nums.length);
    if (unique.size !== nums.length || !contiguous) {
      return "Variables must start at {{1}} with no gaps or repeats";
    }
  }
  if (ADJACENT.test(text)) return "Variables can't be next to each other";
  return null;
};

const validateSuggestions = (list, prefix, errors, { maxReplies } = {}) => {
  const actions = list.filter((s) => s.kind !== "reply").length;
  const replies = list.length - actions;
  if (actions > 4) errors[`${prefix}.suggestions`] = "Max 4 actions";
  else if (maxReplies && replies > maxReplies) {
    errors[`${prefix}.suggestions`] = `Max ${maxReplies} replies`;
  }

  list.forEach((s, i) => {
    const key = `${prefix}.suggestions.${i}`;
    const set = (field, msg) => {
      if (msg && !errors[`${key}.${field}`]) errors[`${key}.${field}`] = msg;
    };

    if (!s.text.trim()) set("text", "Button text is required");
    else if (s.text.length > 25) set("text", "Max 25 characters");
    set("text", checkVariables(s.text));

    if (s.kind === "openUrl") {
      if (!s.url.trim()) set("url", "URL is required");
      set("url", checkVariables(s.url));
    }
    if (s.kind === "dial") {
      if (!s.phone.trim()) set("phone", "Phone number is required");
      set("phone", checkVariables(s.phone));
    }
    if (s.kind === "calendar") {
      ["startTime", "endTime"].forEach((f) => {
        if (!SINGLE_VAR.test(String(s[f] || "").trim())) set(f, "Must be a variable like {{1}}");
      });
      if (!s.title.trim()) set("title", "Event title is required");
      set("title", checkVariables(s.title));
      set("description", checkVariables(s.description));
    }
  });
};

const validateMediaUrl = (value, key, errors) => {
  const v = String(value || "").trim();
  if (!v) return;
  if (!URL_RE.test(v) && !SINGLE_VAR.test(v)) errors[key] = "Enter a URL or {{1}}";
};

const validateCard = (card, prefix, errors, opts) => {
  const set = (field, msg) => {
    if (msg && !errors[`${prefix}.${field}`]) errors[`${prefix}.${field}`] = msg;
  };

  if (!card.title.trim() && !card.description.trim()) {
    set("title", "Add a title or description");
    set("description", "Add a title or description");
  }

  if (card.title.length > 200) set("title", "Max 200 characters");
  set("title", checkVariables(card.title));
  if (countVariables(card.title) > 1) set("title", "Only 1 variable allowed");

  if (card.description.length > 2000) set("description", "Max 2000 characters");
  set("description", checkVariables(card.description));
  if (countVariables(card.description) > 5) set("description", "Max 5 variables");

  validateMediaUrl(card.mediaUrl, `${prefix}.mediaUrl`, errors);
  validateMediaUrl(card.thumbnailUrl, `${prefix}.thumbnailUrl`, errors);
  if (card.thumbnailUrl.trim() && !card.mediaUrl.trim()) {
    set("mediaUrl", "Add a media URL to use a thumbnail");
  }

  validateSuggestions(card.suggestions, prefix, errors, opts);
};

const suggestionVariableCount = (s) =>
  countVariables(s.text) +
  (s.kind === "openUrl" ? countVariables(s.url) : 0) +
  (s.kind === "dial" ? countVariables(s.phone) : 0) +
  (s.kind === "calendar"
    ? countVariables(s.startTime) +
      countVariables(s.endTime) +
      countVariables(s.title) +
      countVariables(s.description)
    : 0);

export const validateTemplateForm = (form) => {
  const errors = {};
  const name = form.template_name.trim();
  if (!name) errors.template_name = "Name is required";
  else if (name.length > 100) errors.template_name = "Max 100 characters";

  if (form.type === "basic") {
    const { text, suggestions } = form.basic;
    if (!text.trim()) errors["basic.text"] = "Message text is required";
    else {
      const msg = checkVariables(text);
      if (msg) errors["basic.text"] = msg;
    }
    validateSuggestions(suggestions, "basic", errors);

    const total =
      countVariables(text) +
      suggestions.reduce((sum, s) => sum + suggestionVariableCount(s), 0);
    if (total > 5 && !errors["basic.text"]) {
      errors["basic.text"] = "Basic templates allow max 5 variables";
    }
  }

  if (form.type === "standalone") {
    validateCard(form.standalone.card, "standalone.card", errors, { maxReplies: 11 });
  }

  if (form.type === "carousel") {
    if (form.carousel.cards.length < 2) errors["carousel.cards"] = "Add at least 2 cards";
    form.carousel.cards.forEach((card, i) =>
      validateCard(card, `carousel.cards.${i}`, errors)
    );
  }

  return errors;
};

// Maps a backend `errors.data` path (a JioCX data path such as
// "content.richCardDetails.carousel.contents.1.cardTitle") to a form field key,
// or null when it can't be mapped.
const DATA_FIELD_TO_FORM = {
  cardTitle: "title",
  cardDescription: "description",
  fileUrl: "mediaUrl",
  thumbnailUrl: "thumbnailUrl",
  url: "url",
  phoneNumber: "phone",
  startTime: "startTime",
  endTime: "endTime",
  title: "title",
  description: "description",
};

const splitPath = (path) => String(path || "").split(/[.[\]]+/).filter(Boolean);

export const dataPathToFormKey = (path, type) => {
  const seg = splitPath(path);
  if (!seg.length) return null;

  let prefix;
  const ci = seg.indexOf("contents");
  if (type === "carousel") {
    if (ci === -1 || !/^\d+$/.test(seg[ci + 1] || "")) return null;
    prefix = `carousel.cards.${seg[ci + 1]}`;
  } else if (type === "standalone") {
    prefix = "standalone.card";
  } else {
    prefix = "basic";
  }

  const si = seg.lastIndexOf("suggestions");
  const last = seg[seg.length - 1];
  if (si !== -1 && /^\d+$/.test(seg[si + 1] || "")) {
    const key = `${prefix}.suggestions.${seg[si + 1]}`;
    if (last === "plainText") return `${key}.text`;
    if (last === "data" || last === "postBack") return `${key}.text`;
    return DATA_FIELD_TO_FORM[last] ? `${key}.${DATA_FIELD_TO_FORM[last]}` : `${key}.text`;
  }
  if (last === "suggestions") return `${prefix}.suggestions`;
  if (type === "basic") return last === "plainText" ? "basic.text" : null;
  if (last === "contents" && type === "carousel") return "carousel.cards";
  return DATA_FIELD_TO_FORM[last] ? `${prefix}.${DATA_FIELD_TO_FORM[last]}` : null;
};

// ---------------------------------------------------------------------------
// Send helpers
// ---------------------------------------------------------------------------

export const normalizeMobile = (str) => {
  const cleaned = String(str ?? "").replace(/[\s-]/g, "");
  const match = cleaned.match(/^(?:\+?91)?([6-9]\d{9})$/);
  return match ? match[1] : null;
};

const LABELS = {
  plainText: "Message",
  cardTitle: "Title",
  cardDescription: "Description",
  fileUrl: "Media URL",
  thumbnailUrl: "Thumbnail URL",
  url: "Button link",
  phoneNumber: "Button phone",
  startTime: "Event start",
  endTime: "Event end",
  title: "Event title",
  description: "Event description",
};

export const fieldLabel = (path) => {
  const seg = splitPath(path);
  const parts = [];
  const ci = seg.indexOf("contents");
  if (ci !== -1 && /^\d+$/.test(seg[ci + 1] || "")) parts.push(`Card ${Number(seg[ci + 1]) + 1}`);
  const si = seg.lastIndexOf("suggestions");
  const inButton = si !== -1 && /^\d+$/.test(seg[si + 1] || "");
  if (inButton) parts.push(`Button ${Number(seg[si + 1]) + 1}`);

  const last = seg[seg.length - 1];
  let label = LABELS[last] || last || "";
  if (inButton && last === "plainText") label = "Text";
  parts.push(label);
  return parts.join(" · ");
};

export const sheetColumn = (path, n) => `${path}#${n}`;

export const sampleHeaders = (variableFields = []) => [
  "mobile",
  ...variableFields.flatMap(({ path, count }) =>
    Array.from({ length: count }, (_, i) => sheetColumn(path, i + 1))
  ),
];

const cellString = (value) =>
  value === undefined || value === null ? "" : String(value).trim();

// Converts rows keyed by `mobile` and `path#n` into send recipients.
// `rowNumber(index)` gives the number shown in error messages.
export const rowsToRecipients = (rows, variableFields = [], rowNumber = (i) => i + 1) => {
  const recipients = [];
  const errors = [];
  // rowNumbers[i] is the display row of recipients[i], for mapping send errors back.
  const rowNumbers = [];

  rows.forEach((row, index) => {
    const rowNo = rowNumber(index);
    const rowErrors = [];
    const mobile = normalizeMobile(cellString(row.mobile));
    if (!mobile) rowErrors.push(`invalid mobile number "${cellString(row.mobile)}"`);

    const variables = {};
    variableFields.forEach(({ path, count }) => {
      variables[path] = [];
      for (let n = 1; n <= count; n++) {
        const column = sheetColumn(path, n);
        const value = cellString(row[column]);
        if (!value) rowErrors.push(`${fieldLabel(path)} {{${n}}} is missing`);
        else if (value.length > MAX_VALUE_LENGTH) {
          rowErrors.push(`${fieldLabel(path)} {{${n}}} is over ${MAX_VALUE_LENGTH} characters`);
        }
        variables[path].push(value);
      }
    });

    if (rowErrors.length) {
      errors.push({ row: rowNo, index, messages: rowErrors });
      return;
    }
    const recipient = { mobile };
    if (variableFields.length) recipient.variables = variables;
    recipients.push(recipient);
    rowNumbers.push(rowNo);
  });

  return { recipients, errors, rowNumbers };
};

const isBlankRow = (row) => Object.values(row || {}).every((v) => cellString(v) === "");

// Rows come from XLSX.utils.sheet_to_json; sheet row numbers are 1-based with
// the header as row 1, so data row i is sheet row i + 2.
export const recipientsFromSheet = (rows, variableFields = []) => {
  const indexed = rows
    .map((row, i) => ({ row, sheetRow: (row.__rowNum__ ?? i + 1) + 1 }))
    .filter(({ row }) => !isBlankRow(row));
  return rowsToRecipients(
    indexed.map(({ row }) => row),
    variableFields,
    (i) => indexed[i].sheetRow
  );
};
